import { useCallback, useEffect, useState } from "react";
import { api } from "../../api.js";
import {
  Button, EmptyState, Field, LoadingRows, Notice, Table, TableCell, TableHead,
  TableHeader, TableRow,
} from "../../components/ui.jsx";
import CompanyDetailPanel from "./CompanyDetailPanel.jsx";
import PaginationControls from "./PaginationControls.jsx";
import { formatDate } from "./utils.js";

const PAGE_SIZE = 25;

export default function CompaniesTab() {
  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState({ search: "", offset: 0 });
  const [selected, setSelected] = useState(null);
  const [state, setState] = useState({ loading: true, companies: [], pagination: null, error: "" });

  const load = useCallback(async () => {
    setState((current) => ({ ...current, loading: true, error: "" }));
    try {
      const data = await api.platformCatalogCompanies({
        limit: PAGE_SIZE,
        offset: filters.offset,
        ...(filters.search ? { search: filters.search } : {}),
      });
      setState({ loading: false, companies: data.companies, pagination: data.pagination, error: "" });
    } catch (error) {
      setState((current) => ({ ...current, loading: false, error: error.message }));
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  function search(event) {
    event.preventDefault();
    setSelected(null);
    setFilters({ search: query.trim(), offset: 0 });
  }

  return (
    <div className="grid gap-6">
      <form className="flex max-w-[620px] gap-2" onSubmit={search} role="search">
        <Field
          className="text-base sm:text-sm"
          label="Search the global catalog"
          placeholder="Company name or domain"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <Button className="mt-[27px]" type="submit">Search</Button>
      </form>
      {state.error && <Notice>{state.error}</Notice>}
      {state.loading ? <LoadingRows count={6} /> : state.companies.length ? (
        <>
          <Table>
            <TableHead><TableHeader>Company</TableHeader><TableHeader>Global values</TableHeader><TableHeader>Workspaces</TableHeader><TableHeader>Latest observation</TableHeader><TableHeader><span className="sr-only">Open</span></TableHeader></TableHead>
            <tbody>{state.companies.map((company) => (
              <TableRow key={company.id}>
                <TableCell>
                  <button className="grid min-w-[180px] cursor-pointer gap-1 bg-transparent text-left" onClick={() => setSelected(company.id)}>
                    <strong className="text-[13px] text-foreground">{company.name}</strong>
                    <small className="text-[11px] text-muted-foreground">{company.domain}</small>
                  </button>
                </TableCell>
                <TableCell><span className="font-bold">{company.current_value_count}</span><span className="text-muted-foreground"> / {company.fields.length}</span></TableCell>
                <TableCell>{company.workspace_count}<span className="text-muted-foreground"> ({company.workspace_record_count} records)</span></TableCell>
                <TableCell className="whitespace-nowrap text-muted-foreground">{formatDate(company.last_observed_at)}</TableCell>
                <TableCell className="text-right"><Button variant="ghost" className="px-2" onClick={() => setSelected(company.id)}>{selected === company.id ? "Selected" : "View"}</Button></TableCell>
              </TableRow>
            ))}</tbody>
          </Table>
          <PaginationControls pagination={state.pagination} onChange={(offset) => { setSelected(null); setFilters((current) => ({ ...current, offset })); }} />
        </>
      ) : <EmptyState title="No catalog companies found" description="Try another name or domain. Companies appear here after a workspace saves a domain-backed company." />}
      {selected && <CompanyDetailPanel id={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
